import { useEffect, useRef, useState, useCallback } from "react";
import Cookies from "universal-cookie";
import _ from "lodash";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { userActions } from "../actions/auth";
import { useDispatch } from "react-redux";

const events = ["mousemove", "keydown", "mousedown", "scroll", "touchstart"];

const AutoLogout = ({
  timeout = 30 * 60 * 1000,
  alertTimeout = 25 * 60 * 1000,
}) => {
  const cookies = new Cookies();
  const dispatch = useDispatch();
  const timeoutRef = useRef();
  const alertRef = useRef();
  const logoutRef = useRef();
  const intervalRef = useRef();
  const cookieRef = useRef();

  //state variables
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [show, setShow] = useState(false);
  const [showExpiredWarning, setShowExpiredWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(timeout - alertTimeout);
  const [stayLoggedIn, setStayLoggedIn] = useState(false); //  state to track if the user clicked "Stay Logged In"

  const handleShow = () => {
    setStayLoggedIn(false);
    setShow(true);
    // Stop listening to events when the modal is shown so that the user can interact with it without resetting the timer
    events.forEach((event) =>
      window.removeEventListener(event, debouncedResetTimer)
    );
  };

  const handleClose = () => {
    setShow(false);
    events.forEach((event) =>
      window.addEventListener(event, debouncedResetTimer)
    );
  };

  const checkLogin = () => {
    const cookieExpiryTime = checkCookieExpiry();
    const authToken = cookies.get("JWT");

    if (!cookieExpiryTime && !authToken && isLoggedIn) {
      handleCookieExpiry();
    } else if (cookieExpiryTime && authToken && !isLoggedIn) {
      setIsLoggedIn(true);
    }
  };

  const checkCookieExpiry = () => {
    const cookieExpiry = cookies.get("JWT_Expiry");

    if (!cookieExpiry) {
      return null; // Cookie not found
    } else {
      return cookieExpiry;
    }
  };

  const handleCookieExpiry = () => {
    console.log("cookies expired");
    setShowExpiredWarning(true);
    logoutRef.current = setTimeout(() => {
      setShowExpiredWarning(false);
      handleLogout();
    }, 3500);
  };

  const triggerAlert = (startTime) => {
    const time = timeout - (Date.now() - startTime);
    const cookieExpiry = checkCookieExpiry();
    const cookieExpiryTime = new Date(cookieExpiry).getTime();
    const alertCountdown = timeout - alertTimeout;

    if (
      time > 0 &&
      cookieExpiry &&
      cookieExpiryTime > Date.now() + alertCountdown // Only show alert if cookie expiry is more than alert countdown time so cookie doesn't expire before alert
    ) {
      // Start the interval to update the time left every second
      intervalRef.current = setInterval(() => {
        const timeLeft = timeout - (Date.now() - startTime);
        setTimeLeft(timeLeft);
        if (timeLeft <= 0) {
          clearInterval(intervalRef.current);
        }
      }, 1000);

      handleShow();
    } else {
      cookieRef.current = setTimeout(() => {
        handleCookieExpiry();
      }, cookieExpiryTime - Date.now());
    }
  };

  const handleLogout = () => {
    dispatch(userActions.logout());
  };

  const resetTimer = useCallback(() => {
    checkLogin();

    clearTimeout(timeoutRef.current);
    clearTimeout(alertRef.current);
    setTimeLeft(timeout - alertTimeout - 1000); //subtracting 1 second to account for the time taken to show the modal
    clearInterval(intervalRef.current);

    if (isLoggedIn) {
      console.log("resetting inactivity timer...");
      const startTime = Date.now();

      alertRef.current = setTimeout(
        () => triggerAlert(startTime),
        alertTimeout
      );
      timeoutRef.current = setTimeout(() => {
        setTimeLeft(0);
        handleLogout();
      }, timeout);
    }
  }, [timeout, isLoggedIn]);

  // Debounced version of resetTimer
  const debouncedResetTimer = useCallback(_.debounce(resetTimer, 300), [
    resetTimer,
  ]);

  useEffect(() => {
    // Check if the user is logged in and start the timers on mount
    checkLogin();
    debouncedResetTimer();

    events.forEach((event) =>
      window.addEventListener(event, debouncedResetTimer)
    );

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, debouncedResetTimer)
      );
      clearTimeout(timeoutRef.current);
      clearTimeout(alertRef.current);
      clearInterval(intervalRef.current);
    };
  }, [debouncedResetTimer]);

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        backdrop='static'
        keyboard={false}
        centered
      >
        <Modal.Header
          style={{ borderBottomColor: "#F08021", borderBottomWidth: "4px" }}
          closeButton
        >
          <strong className='me-auto'>Still with us?</strong>
        </Modal.Header>
        <Modal.Body
          className='d-flex flex-column text-center justify-content-center align-items-center'
          style={{ minHeight: "200px" }}
        >
          {timeLeft > 0 ? (
            stayLoggedIn ? (
              <></> // After the user clicks "Stay Logged In", we don't show anything
            ) : (
              <>
                <div className='fs-4 fw-bold'>
                  <i className='bi bi-stopwatch' /> Your session will expire in:{" "}
                </div>
                <div className='fs-1 fw-bold'>
                  {Math.floor(timeLeft / 60 / 1000)} minutes{" "}
                  {Math.floor(
                    (timeLeft / 60 / 1000 - Math.floor(timeLeft / 60 / 1000)) *
                      60
                  )}{" "}
                  seconds
                </div>
                <br />
                <p>
                  Your privacy is important to us, and you've been inactive for
                  a while.
                  <br />
                  Please choose "Stay Logged In" to continue your session.
                </p>
              </>
            )
          ) : (
            <div className='fs-1 fw-bold'>Logging out...</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='outline-secondary'
            onClick={() => {
              handleClose();
              handleLogout();
            }}
          >
            Logout
          </Button>
          <Button
            variant='primary'
            onClick={() => {
              setStayLoggedIn(true);
              handleClose();
              resetTimer();
            }}
          >
            Stay Logged In
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showExpiredWarning}
        onHide={() => {
          setShowExpiredWarning(false);
          clearTimeout(logoutRef.current);
        }}
        backdrop='static'
        keyboard={false}
        centered
      >
        <Modal.Header
          style={{ borderBottomColor: "#F08021", borderBottomWidth: "4px" }}
        >
          <strong className='me-auto'>Session Expiry</strong>
        </Modal.Header>
        <Modal.Body
          className='d-flex flex-column text-center justify-content-center align-items-center'
          style={{ minHeight: "200px" }}
        >
          <div className='fs-4 fw-bold'>
            <i className='bi bi-stopwatch' /> Your session has expired.
          </div>
          <div className='fs-1 fw-bold'>Logging out...</div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AutoLogout;
